
import { useState } from "react";
import { 
  UserPlus, 
  Users, 
  Check, 
  X, 
  Mail, 
  Shield, 
  AlertCircle,
  Trash2,
  Edit
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCollaborators } from "@/hooks/use-collaborators";
import { CollaboratorPermissions } from "@/types/collaborator";

export default function Collaborators() {
  const { 
    collaborators, 
    invites, 
    isLoading, 
    inviteCollaborator, 
    removeCollaborator, 
    cancelInvite,
    updateCollaboratorPermissions 
  } = useCollaborators();
  
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newInvite, setNewInvite] = useState({
    email: "",
    permissions: {
      posts: true,
      clients: true,
      reports: true,
      calendar: true,
      finances: false, // Sempre false por padrão
      workflow: true,
      approvals: true,
      automation: true
    } as CollaboratorPermissions
  });
  
  const handleSendInvite = async () => {
    if (!newInvite.email || isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const success = await inviteCollaborator(newInvite.email, newInvite.permissions);
      if (success) {
        setInviteDialogOpen(false);
        setNewInvite({
          email: "",
          permissions: {
            posts: true,
            clients: true,
            reports: true,
            calendar: true,
            finances: false,
            workflow: true,
            approvals: true,
            automation: true
          }
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleEditPermissions = (collaborator: any) => {
    setSelectedCollaborator(collaborator);
    setEditDialogOpen(true);
  };
  
  const handleUpdatePermissions = async () => {
    if (selectedCollaborator) {
      const success = await updateCollaboratorPermissions(
        selectedCollaborator.id, 
        selectedCollaborator.permissions
      );
      if (success) {
        setEditDialogOpen(false);
        setSelectedCollaborator(null);
      }
    }
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500"><Check className="h-3 w-3 mr-1" />Ativo</Badge>;
      case "pending":
        return <Badge variant="outline" className="text-yellow-600 border-yellow-300"><Mail className="h-3 w-3 mr-1" />Pendente</Badge>;
      case "inactive":
        return <Badge variant="outline" className="text-red-600 border-red-300"><X className="h-3 w-3 mr-1" />Inativo</Badge>;
      default:
        return <Badge className="bg-gray-500">{status}</Badge>;
    }
  };

  const renderPermissionsList = (permissions: CollaboratorPermissions) => {
    const activePermissions = Object.entries(permissions)
      .filter(([_, value]) => Boolean(value))
      .map(([key, _]) => {
        const labels: Record<string, string> = {
          posts: 'Posts',
          clients: 'Clientes',
          reports: 'Relatórios',
          calendar: 'Calendário',
          finances: 'Financeiro',
          workflow: 'Fluxo de Trabalho',
          approvals: 'Aprovações',
          automation: 'Automação'
        };
        return labels[key] || key;
      });

    return (
      <div className="flex flex-wrap gap-1">
        {activePermissions.map(permission => (
          <Badge key={permission} variant="secondary" className="text-xs">
            {permission}
          </Badge>
        ))}
      </div>
    );
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Users className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Carregando colaboradores...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h2 className="text-2xl font-bold">Colaboradores</h2>
          <p className="text-muted-foreground">
            Gerencie quem tem acesso aos seus dados (exceto financeiro)
          </p>
        </div>
        
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button className="mt-4 md:mt-0">
              <UserPlus className="h-4 w-4 mr-2" />
              Convidar Colaborador
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Convidar Colaborador</DialogTitle>
              <DialogDescription>
                Envie um convite para alguém colaborar com você. Eles terão acesso a tudo exceto dados financeiros.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  className="col-span-3"
                  value={newInvite.email}
                  onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                />
              </div>
              
              <Separator />
              
              <div className="space-y-3">
                <Label className="text-sm font-medium">Permissões</Label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(newInvite.permissions).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      posts: 'Posts',
                      clients: 'Clientes',
                      reports: 'Relatórios',
                      calendar: 'Calendário',
                      finances: 'Financeiro',
                      workflow: 'Fluxo de Trabalho',
                      approvals: 'Aprovações',
                      automation: 'Automação'
                    };
                    
                    return (
                      <div key={key} className="flex items-center space-x-2">
                        <Switch
                          id={key}
                          checked={Boolean(value)}
                          disabled={key === 'finances'} // Financeiro sempre desabilitado
                          onCheckedChange={(checked) => 
                            setNewInvite({
                              ...newInvite,
                              permissions: {
                                ...newInvite.permissions,
                                [key]: checked
                              }
                            })
                          }
                        />
                        <Label htmlFor={key} className="text-sm">
                          {labels[key]}
                          {key === 'finances' && (
                            <span className="text-xs text-red-500 ml-1">(Bloqueado)</span>
                          )}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSendInvite}
                disabled={!newInvite.email || !newInvite.email.includes('@') || isSubmitting}
              >
                {isSubmitting ? "Enviando..." : "Enviar Convite"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Convites Pendentes */}
      {invites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Convites Pendentes
            </CardTitle>
            <CardDescription>
              Convites enviados aguardando resposta
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invites.map((invite) => (
                <div key={invite.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-blue-500" />
                    <div>
                      <p className="font-medium">{invite.email}</p>
                      <p className="text-sm text-muted-foreground">
                        Enviado em {new Date(invite.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {renderPermissionsList(invite.permissions)}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelInvite(invite.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Colaboradores Ativos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Colaboradores Ativos
          </CardTitle>
          <CardDescription>
            Pessoas que têm acesso aos seus dados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {collaborators.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhum colaborador ainda</p>
              <p className="text-sm text-muted-foreground">
                Convide pessoas para colaborar com você
              </p>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Permissões</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collaborators.map((collaborator) => (
                    <TableRow key={collaborator.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {collaborator.collaborator_name || 'Usuário'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {collaborator.collaborator_email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(collaborator.status)}
                      </TableCell>
                      <TableCell>
                        {renderPermissionsList(collaborator.permissions)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Shield className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditPermissions(collaborator)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Editar Permissões
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => removeCollaborator(collaborator.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para editar permissões */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Permissões</DialogTitle>
            <DialogDescription>
              Ajuste as permissões deste colaborador
            </DialogDescription>
          </DialogHeader>
          
          {selectedCollaborator && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(selectedCollaborator.permissions).map(([key, value]) => {
                  const labels: Record<string, string> = {
                    posts: 'Posts',
                    clients: 'Clientes',
                    reports: 'Relatórios',
                    calendar: 'Calendário',
                    finances: 'Financeiro',
                    workflow: 'Fluxo de Trabalho',
                    approvals: 'Aprovações',
                    automation: 'Automação'
                  };
                  
                  return (
                    <div key={key} className="flex items-center space-x-2">
                      <Switch
                        id={key}
                        checked={Boolean(value)}
                        disabled={key === 'finances'} // Financeiro sempre desabilitado
                        onCheckedChange={(checked) => 
                          setSelectedCollaborator({
                            ...selectedCollaborator,
                            permissions: {
                              ...selectedCollaborator.permissions,
                              [key]: checked
                            }
                          })
                        }
                      />
                      <Label htmlFor={key} className="text-sm">
                        {labels[key]}
                        {key === 'finances' && (
                          <span className="text-xs text-red-500 ml-1">(Bloqueado)</span>
                        )}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdatePermissions}>
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Informações sobre permissões */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Sobre as Permissões
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <p>
              <strong>Colaboradores têm acesso a:</strong> Todos os seus dados de clientes, 
              posts, aprovações, calendário, fluxo de trabalho e relatórios.
            </p>
            <p className="text-red-600">
              <strong>Restrição:</strong> Os dados financeiros são sempre privados e 
              nunca são compartilhados com colaboradores.
            </p>
            <p className="text-muted-foreground">
              Você pode ajustar as permissões específicas para cada colaborador a qualquer momento.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
